import React, {useState, useCallback, useId} from 'react';
import {useTranslation} from 'react-i18next';
import {FaIcon} from '../../FaIcon';
import {PromptModalityChoice} from '../../prompts/PromptModalityChoice';
import {PromptVersionBadge} from '../../prompts/PromptVersionBadge';
import {pingPromptCopied} from '../../prompts/promptCopyPing';
import {
    buildModalityOptions,
    defaultPromptMode,
    resolveActivePrompt,
} from '../../prompts/promptModalityOptions';
import {listClients} from '../utils/mcpClient';

function CopyButton({text}) {
    const {t} = useTranslation();
    const [copied, setCopied] = useState(false);

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    }, [text]);

    return (
        <button className="copy-btn" onClick={handleCopy}>
            {copied ? t('dashbo_mcp.setup.copied') : <FaIcon iconName="copy"/>}
        </button>
    );
}

export function SetupGuideCard({icon, titleKey, descKey, noteKey, children}) {
    const {t} = useTranslation();
    const [expanded, setExpanded] = useState(false);

    return (
        <div className="setup-guide-card">
            <div className="card-header-row" onClick={() => setExpanded(!expanded)}>
                <div className="card-logo">
                    {typeof icon === 'string' ? <FaIcon iconName={icon}/> : icon}
                </div>
                <div className="card-info">
                    <h4>{t(titleKey)}</h4>
                    <p>{t(descKey)}</p>
                </div>
                <span className={`expand-icon ${expanded ? 'expanded' : ''}`}>
                    <FaIcon iconName="chevron-down"/>
                </span>
            </div>
            {expanded && <div className="card-body">
                {noteKey && <div className="setup-guide-note">
                    <FaIcon iconName="exclamation-triangle"/> {t(noteKey)}
                </div>}
                {children}
            </div>}
        </div>
    );
}

export function Step({number, children}) {
    return (
        <div className="step">
            <span className="step-number">{number}</span>
            <div className="step-content">{children}</div>
        </div>
    );
}

export function CodeBlock({children}) {
    const text = typeof children === 'string' ? children : '';
    return (
        <div className="code-block">
            <CopyButton text={text}/>
            {children}
        </div>
    );
}

export function UrlBlock({children}) {
    return <span className="url-block">{children}</span>;
}

// Bloque copiar+preview del prompt del analista. Hoy se usa en UN solo contexto:
// dentro del colapsable "Avanzado" (OptionalPromptStep), en todas las cards de MCP
// —Claude y, desde 2026-09, también ChatGPT—, porque por MCP el brief llega solo.
// Antes ChatGPT lo tenía inline como paso numerado: con un Custom GPT, pegarlo en el
// campo Instrucciones ERA el setup. Ese flujo ya no se documenta.
//
// Ofrece las modalidades multi-cliente que el backend tenga sembradas: stub (canal pull —
// recomendado y default cuando existe), bloque aditivo y pack completo. `prompts` viaja como
// {full, addon, stub} de {id, text, version} (fetchAgencyPrompts); sin addon/stub se comporta
// como siempre — backend y frontend no deployan juntos.
//
// `charLimit` marca el techo del campo destino (ChatGPT: 8.000 caracteres en las Instrucciones
// de un GPT). Si el texto elegido no entra, hay que decirlo ANTES de que el usuario copie y
// pegue: el campo trunca o rechaza, y desde nuestro lado eso es invisible.
export function PromptCopyBlock({prompts, copyStatus, onCopied, noteKey, charLimit}) {
    const {t} = useTranslation();
    const [promptExpanded, setPromptExpanded] = useState(false);
    const [copied, setCopied] = useState(false);
    // El default es DERIVADO (stub si existe, si no ADDON) y la elección explícita lo pisa.
    // No puede ser el initializer de un useState: los textos llegan de un fetch DESPUÉS del
    // primer render, y el default congelado en el mount ignoraría al stub para siempre.
    const [chosenMode, setChosenMode] = useState(null);
    const mode = chosenMode || defaultPromptMode(prompts);
    // Los radios se agrupan por `name` a nivel DOCUMENTO, y esta pantalla monta el bloque tres
    // veces (ChatGPT, Claude Desktop, Claude Web), que además pueden estar abiertas a la vez —
    // comparar una guía contra otra es el uso normal. Con un `name` fijo, el navegador aplicaba
    // exclusividad sobre los seis inputs y la card que no cambió volvía a imponer su `checked`:
    // el radio recién clickeado quedaba en blanco. Un id por instancia los separa.
    const groupName = useId();

    const showChoice = Boolean(prompts && (prompts.addon || prompts.stub));
    const active = resolveActivePrompt(mode, prompts);
    const activeText = active ? active.text : null;

    const lines = activeText ? activeText.split('\n') : [];
    const previewLines = lines.slice(0, 5).join('\n');
    const hasMore = lines.length > 5;
    const exceedsLimit = Boolean(charLimit && activeText && activeText.length > charLimit);

    const handleCopy = useCallback(() => {
        if (!activeText) return;
        navigator.clipboard.writeText(activeText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            // Ping de copiado (fire-and-forget, patrón PromptLibraryCard): solo si la copia
            // ocurrió de verdad, con el id/versión de la modalidad ACTIVA.
            if (active.id) {
                pingPromptCopied(active.id, {version: active.version});
                if (onCopied) onCopied(active.id, active.version);
            }
        }).catch(() => {});
    }, [active, activeText, onCopied]);

    if (!prompts || !prompts.full || !prompts.full.text) return null;

    return (
        <div className="prompt-copy-block">
            {showChoice && (
                <PromptModalityChoice
                    name={groupName}
                    question={t('dashbo_mcp.setup.modality_question')}
                    options={buildModalityOptions({t, prompts})}
                    value={mode}
                    onChange={setChosenMode}
                />
            )}
            {exceedsLimit && (
                <div className="setup-guide-note">
                    <FaIcon iconName="exclamation-triangle"/>
                    {t('dashbo_mcp.setup.prompt_too_long', {
                        count: activeText.length,
                        limit: charLimit,
                    })}
                </div>
            )}
            <div className="prompt-copy-actions">
                <button className="btn -alt -negative btn-sm" onClick={handleCopy}>
                    {copied ? t('dashbo_mcp.setup.copied') : t('dashbo_mcp.setup.copy_prompt')}
                </button>
                {active && active.id && (
                    <PromptVersionBadge status={copyStatus && copyStatus[active.id]}/>
                )}
            </div>
            <div className="prompt-preview">
                <pre className="prompt-preview-text">
                    {promptExpanded ? activeText : previewLines}{!promptExpanded && hasMore ? '...' : ''}
                </pre>
                {hasMore && (
                    <button className="prompt-expand-btn" onClick={() => setPromptExpanded(!promptExpanded)}>
                        {promptExpanded
                            ? t('dashbo_mcp.setup.collapse_prompt')
                            : t('dashbo_mcp.setup.expand_prompt', {count: lines.length})}
                    </button>
                )}
            </div>
            {noteKey && <p className="optional-step-note">{t(noteKey)}</p>}
        </div>
    );
}

export function OptionalPromptStep({prompts, copyStatus, onCopied, children}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);

    if (!prompts || !prompts.full || !prompts.full.text) return null;

    return (
        <div className="optional-prompt-step">
            <button className="optional-step-toggle" onClick={() => setOpen(!open)}>
                <FaIcon iconName={open ? 'chevron-down' : 'chevron-right'}/>
                {t('dashbo_mcp.setup.optional_prompt_title')}
            </button>
            {open && (
                <div className="optional-step-content">
                    <p className="optional-step-hint">{children}</p>
                    <PromptCopyBlock
                        prompts={prompts}
                        copyStatus={copyStatus}
                        onCopied={onCopied}
                        noteKey="dashbo_mcp.setup.prompt_benefit_note"
                    />
                </div>
            )}
        </div>
    );
}

export function TestTokenButton() {
    const {t} = useTranslation();
    const [state, setState] = useState({kind: 'idle'});

    const handleTest = useCallback(async () => {
        setState({kind: 'loading'});
        try {
            const data = await listClients();
            const clientCount = Array.isArray(data?.clients) ? data.clients.length : undefined;
            setState({kind: 'ok', clientCount});
        } catch (e) {
            let code = 'request_error';
            if (e?.message?.includes('401') || e?.message?.includes('403')) code = 'unauthorized';
            else if (e?.message?.includes('5')) code = 'server_error';
            else if (!navigator.onLine) code = 'network_error';
            setState({kind: 'error', code});
        }
    }, []);

    return (
        <div className="test-token-row">
            <button
                className="btn -alt -negative btn-sm"
                onClick={handleTest}
                disabled={state.kind === 'loading'}
            >
                <FaIcon iconName={state.kind === 'loading' ? 'spinner' : 'check-circle'}/>
                {state.kind === 'loading' ? t('dashbo_mcp.setup.test_token_loading') : t('dashbo_mcp.setup.test_token_button')}
            </button>
            {state.kind === 'ok' && (
                <span className="test-token-result -ok">
                    <FaIcon iconName="check"/>
                    {t('dashbo_mcp.setup.test_token_ok')}
                    {typeof state.clientCount === 'number' && (
                        <span className="test-token-detail">
                            {' '}— {t('dashbo_mcp.setup.test_token_clients_accessible', {count: state.clientCount})}
                        </span>
                    )}
                </span>
            )}
            {state.kind === 'error' && (
                <span className="test-token-result -error">
                    <FaIcon iconName="xmark"/>
                    {t(`dashbo_mcp.setup.test_token_error_${state.code}`)}
                </span>
            )}
        </div>
    );
}

export function ChatGptCommonErrors() {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);

    // Los cuatro anteriores (dominio duplicado, plan para crear GPTs, parseo del
    // OpenAPI, token mal pegado) eran del Custom GPT y ninguno puede pasar ya por
    // MCP + OAuth. Estos son los del flujo nuevo, por orden de frecuencia real.
    // Desde el 05/10/2026 ChatGPT ya no pide el Modo desarrollador: el servidor se agrega desde
    // Complementos → Agregar → «Agregar servidor MCP personalizado» (verificado en vivo el 14/10).
    // `no_add_mcp` reemplaza a `no_plus` (el «+» de Ajustes → Complementos, Hesta 23/09), que ya no
    // existe. `no_dev_mode` queda al final para quien llega con una guía vieja: le dice que ya no hace
    // falta y adónde ir.
    const errors = [
        'no_add_mcp',
        'oauth_failed',
        'not_used',
        'write_plan',
        'no_dev_mode',
    ];

    return (
        <div className="optional-prompt-step common-errors-step">
            <button className="optional-step-toggle" onClick={() => setOpen(!open)}>
                <FaIcon iconName={open ? 'chevron-down' : 'chevron-right'}/>
                <FaIcon iconName="exclamation-triangle"/>
                {t('dashbo_mcp.setup.common_errors_title')}
            </button>
            {open && (
                <div className="optional-step-content">
                    <ul className="common-errors-list">
                        {errors.map((key) => (
                            <li key={key}>
                                <strong dangerouslySetInnerHTML={{__html: t(`dashbo_mcp.setup.common_error_${key}_title`)}}/>
                                <span className="common-error-desc" dangerouslySetInnerHTML={{__html: t(`dashbo_mcp.setup.common_error_${key}_desc`)}}/>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

export function OptionalSkillStep() {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);

    return (
        <div className="optional-prompt-step">
            <button className="optional-step-toggle" onClick={() => setOpen(!open)}>
                <FaIcon iconName={open ? 'chevron-down' : 'chevron-right'}/>
                {t('dashbo_mcp.setup.optional_skill_title')}
            </button>
            {open && (
                <div className="optional-step-content">
                    <p className="optional-step-hint">{t('dashbo_mcp.setup.skill_if_nodejs')}</p>
                    <CodeBlock>npx skills add https://github.com/fede-brc/dashbo-skills --skill dashbo-agencies</CodeBlock>
                    <p className="optional-step-hint" style={{marginTop: '0.6em'}}>{t('dashbo_mcp.setup.skill_if_no_nodejs')}</p>
                    <CodeBlock>mkdir -p .claude/skills/dashbo-agencies && curl -sL https://raw.githubusercontent.com/fede-brc/dashbo-skills/main/skills/dashbo-agencies/SKILL.md -o .claude/skills/dashbo-agencies/SKILL.md</CodeBlock>
                    <p className="optional-step-hint" style={{marginTop: '0.6em'}}>{t('dashbo_mcp.setup.skill_description')}</p>
                    <p className="optional-step-note">{t('dashbo_mcp.setup.skill_benefit_note')}</p>
                </div>
            )}
        </div>
    );
}
